import dayjs from "dayjs";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { ValidatorAction, ValidatorMap } from "ember-changeset/types";
import type {
  ForcModelPojo,
  Payload
} from "parog-web/components/forc/abstract/journal-event-based-component";
import ForcJournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import isChangesetValid from "parog-web/utils/is-changeset-valid";
import { lookupValidator } from "validated-changeset";

interface Score {
  description?: string;
  number: number;
}

type ValidatonNames = string | string[];

interface ScaleSurveyEditionModel extends ForcModelPojo {
  items: unknown[];
  isTotalScoreFilled: boolean;
  total?: number;
}

/**
 * This class is here to help to build some "scale survey" form like FAST, QIDS or YMRS.
 * It works with a JE containing an array of items and a total.
 * But you can override some methods to work with a flatten JE with properties.
 */
export default class ScaleSurveyEditionFormComponent extends ForcJournalEventBasedComponent {
  /// ABSTRACT

  static override forcDisplayName = "Not provided";
  static override fullComponentId = "Not provided";

  override journalEventProperties = [
    "occurredAt",
    "total",
    "items",
    ...this.questionIds
  ];
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;

  get currentValidations(): ValidatorMap {
    return this.changeset.get("isTotalScoreFilled")
      ? { ...this.totalValidations, ...this.occurredAtValidations }
      : { ...this.surveyValidations, ...this.occurredAtValidations };
  }

  get isChangesetValid(): boolean {
    return isChangesetValid(this.changeset, this.currentValidations);
  }

  /// PROPERTIES
  get maximalScore(): number {
    throw `'maximalScore' is not provided for the class '${this.fullComponentId}'.`;
  }

  get numberOfItems(): number {
    throw `'numberOfItems' is not provided for the class '${this.fullComponentId}'.`;
  }

  get occurredAtValidations(): ValidatorMap {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: "La date"
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  get questionIds(): string[] {
    const attributes: string[] = [];
    for (let i = 0; i < this.numberOfItems; i++) {
      attributes.push(i.toString());
    }
    return attributes;
  }

  get score(): Score {
    return this._computeScore(this.changeset);
  }

  get surveyValidations(): ValidatorMap {
    return this.questionIds.reduce((validations, index) => {
      validations[index] = validatePresence({
        presence: true,
        description: this._getValidationName(index)
      });
      return validations;
    }, {});
  }

  get totalValidations(): ValidatorMap {
    return {
      total: [
        validatePresence({
          presence: true,
          description: "Le total"
        }),
        validateNumber({
          integer: true,
          lte: this.maximalScore,
          positive: true,
          description: "Le total"
        })
      ]
    };
  }

  get validationNames(): ValidatonNames {
    return "Le score";
  }

  override get validations(): ValidatorMap {
    return Object.assign({}, this.surveyValidations, this.totalValidations);
  }

  /**
   * The payload will either be a changeset (when template refreshes),
   * or a POJO (when the form is saved).
   *
   * So use the 'get' function from '@ember/object' to access the payload values.
   */
  _computeScore(_payload: Payload): Score {
    throw `'_computeScore()' is not implemented for the class '${this.fullComponentId}'.`;
  }

  _getValidationName(index: string): ValidatonNames {
    if (
      typeof this.validationNames === "string" ||
      this.validationNames instanceof String
    ) {
      return this.validationNames;
    } else if (Array.isArray(this.validationNames)) {
      return this.validationNames[index];
    }
    throw `The 'validationNames' should be a 'string' or an 'array of string'.`;
  }

  /// EVENTS
  override computePayload(): Payload {
    const payload = super.computePayload();

    if (!(this.model as ScaleSurveyEditionModel).isTotalScoreFilled) {
      // If the total is not given, compute it.
      payload.total = this._computeScore(payload).number;
      // Save the answered items
      payload.items = this.questionIds.map(index => payload[index]);
    } else {
      // If the total is given we don't store the items.
      payload.items = [];
    }

    // Remove the question item from the payload
    this.questionIds.forEach(index => delete payload[index]);

    return payload;
  }

  /// HOOKS
  onTabButtonClicked = (type: string): void => {
    this.changeset.set("isTotalScoreFilled", type === "second");
  };

  override retrieveModel(): ScaleSurveyEditionModel {
    const model = super.retrieveModel() as ScaleSurveyEditionModel;

    if (this.isEditMode) {
      // Select the second tab only if the total has been set and not the other properties.
      model.isTotalScoreFilled = model.total != null && !model.items?.length;

      // Inject the items array properties into our model
      this.questionIds.forEach(index => {
        model[index] = model.items[index];
      });
    } else {
      model.occurredAt = new Date();
    }
    return model;
  }

  /// PRIVATE
  override validator: ValidatorAction = obj => {
    return (
      lookupValidator(this.currentValidations) as NonNullable<ValidatorAction>
    )(obj);
  };
}
